<template>
  <div class="state-debugger">
    <div class="debugger-header" @click="toggleExpanded">
      <h3>🔍 State Debugger - New Imprint</h3>
      <span class="toggle-icon">{{ isExpanded ? '−' : '+' }}</span>
    </div>
    
    <div v-if="isExpanded" class="debugger-content">
      <!-- Current Imprint Key -->
      <section class="debug-section">
        <h4>Current Imprint Key</h4>
        <pre>{{ newImprintStore.currentImprintKey }}</pre>
      </section>

      <!-- Total Imprints Count -->
      <section class="debug-section">
        <h4>Total Imprints</h4>
        <pre>{{ newImprintStore.imprints.length }}</pre>
      </section>

      <!-- Current Imprint Data -->
      <section class="debug-section">
        <h4>Current Imprint Data</h4>
        <div class="debug-subsection">
          <strong>Location:</strong>
          <pre>{{ JSON.stringify(currentImprint?.selectedLocation, null, 2) }}</pre>
        </div>
        
        <div class="debug-subsection">
          <strong>Message Type:</strong>
          <pre>{{ currentImprint?.messageType }}</pre>
        </div>
        
        <div class="debug-subsection">
          <strong>Max Lines:</strong>
          <pre>{{ currentImprint?.maxLines }}</pre>
        </div>
        
        <div class="debug-subsection">
          <strong>Text Lines ({{ currentImprint?.textLines?.length || 0 }}):</strong>
          <pre>{{ JSON.stringify(currentImprint?.textLines, null, 2) }}</pre>
        </div>
        
        <div class="debug-subsection">
          <strong>Image Data:</strong>
          <pre>{{ JSON.stringify(currentImprint?.imageData, null, 2) }}</pre>
        </div>
        
        <div class="debug-subsection">
          <strong>Special Instructions:</strong>
          <pre>{{ currentImprint?.specialInstructions || '(empty)' }}</pre>
        </div>
        
        <div class="debug-subsection">
          <strong>Errors:</strong>
          <pre>{{ JSON.stringify(currentImprint?.errors, null, 2) }}</pre>
        </div>
      </section>

      <!-- Apply to All State -->
      <section class="debug-section">
        <h4>Apply to All State</h4>
        <pre>{{ JSON.stringify(newImprintStore.applyToAll, null, 2) }}</pre>
      </section>

      <!-- Max Colors Tracking -->
      <section class="debug-section">
        <h4>Max Colors Tracking (Current Imprint)</h4>
        <div class="debug-subsection">
          <strong>Unique Colors Count:</strong>
          <pre>{{ uniqueColorsCount }}</pre>
        </div>
        <div class="debug-subsection">
          <strong>Max Colors Allowed:</strong>
          <pre>{{ maxColorsTracking?.maxColors || 'N/A' }}</pre>
        </div>
        <div class="debug-subsection">
          <strong>Is Max Reached:</strong>
          <pre>{{ maxColorsTracking?.isMaxReached }}</pre>
        </div>
        <div class="debug-subsection">
          <strong>Unique Color SKUs:</strong>
          <pre>{{ JSON.stringify(maxColorsTracking?.uniqueColors, null, 2) }}</pre>
        </div>
      </section>

      <!-- All Imprints Array -->
      <section class="debug-section">
        <h4>All Imprints Array</h4>
        <pre>{{ JSON.stringify(newImprintStore.imprints, null, 2) }}</pre>
      </section>

      <!-- Imprint Store Selected Method -->
      <section class="debug-section">
        <h4>Selected Imprint Method (from useImprintStore)</h4>
        <pre>{{ JSON.stringify(imprintStore.selectedImprintMethod, null, 2) }}</pre>
      </section>

      <!-- Imprint Store Selected Artwork Method -->
      <section class="debug-section">
        <h4>Selected Artwork Method (from useImprintStore)</h4>
        <pre>{{ imprintStore.selectedArtworkMethod || 'Not selected' }}</pre>
      </section>

      <!-- Available Locations -->
      <section class="debug-section">
        <h4>Available Locations ({{ availableLocations?.length || 0 }})</h4>
        <pre>{{ JSON.stringify(availableLocations, null, 2) }}</pre>
      </section>

      <!-- Available Colors -->
      <section class="debug-section">
        <h4>Available Colors ({{ availableColors?.length || 0 }})</h4>
        <pre>{{ JSON.stringify(availableColors?.slice(0, 5), null, 2) }}{{ availableColors?.length > 5 ? '\n... and ' + (availableColors.length - 5) + ' more' : '' }}</pre>
      </section>

      <!-- Getters Preview -->
      <section class="debug-section">
        <h4>Computed Getters</h4>
        <div class="debug-subsection">
          <strong>Can Add Line:</strong>
          <pre>{{ newImprintStore.canAddLine }}</pre>
        </div>
        <div class="debug-subsection">
          <strong>Has Image Line:</strong>
          <pre>{{ newImprintStore.hasImageLine }}</pre>
        </div>
        <div class="debug-subsection">
          <strong>Custom Color Fee:</strong>
          <pre>{{ newImprintStore.customColorFee }}</pre>
        </div>
      </section>

      <!-- Export Data Preview -->
      <section class="debug-section">
        <h4>Export Data (getImprintData())</h4>
        <pre>{{ JSON.stringify(exportData, null, 2) }}</pre>
      </section>

      <!-- Actions -->
      <section class="debug-section actions">
        <h4>Debug Actions</h4>
        <button @click="testValidation">Test Validation</button>
        <button @click="testAddLine">Add Line</button>
        <button @click="testResetImprint">Reset Imprint</button>
      </section>
    </div>
  </div>
</template>

<script>
import { computed, ref } from 'vue';
import { useNewImprintStore } from '../stores/useNewImprintStore';
import { useImprintStore } from '../stores/useImprintStore';

export default {
  name: 'StateDebuggerNewImprint',
  
  setup() {
    const newImprintStore = useNewImprintStore();
    const imprintStore = useImprintStore();
    const isExpanded = ref(false);

    // Current imprint data
    const currentImprint = computed(() => newImprintStore.currentImprint);
    
    // Max colors tracking
    const maxColorsTracking = computed(() => 
      newImprintStore.maxColorsTracking[newImprintStore.currentImprintKey]
    );
    
    const uniqueColorsCount = computed(() => 
      newImprintStore.currentUniqueColorsCount
    );

    // Available data from main store
    const availableLocations = computed(() => newImprintStore.availableLocations);
    const availableColors = computed(() => newImprintStore.availableColors);

    // Export data
    const exportData = computed(() => newImprintStore.getImprintData());

    // Toggle expanded state
    const toggleExpanded = () => {
      isExpanded.value = !isExpanded.value;
    };

    // Test actions
    const testValidation = () => {
      const isValid = newImprintStore.validate();
      console.log('[StateDebugger] Validation result:', isValid);
      console.log('[StateDebugger] Validation errors:', currentImprint.value?.errors);
    };

    const testAddLine = () => {
      if (newImprintStore.canAddLine) {
        newImprintStore.addTextLine();
        console.log('[StateDebugger] Line added');
      } else {
        console.log('[StateDebugger] Cannot add line - max lines reached');
      }
    };

    const testResetImprint = () => {
      if (confirm('Reset current imprint?')) {
        newImprintStore.resetCurrentImprint();
        console.log('[StateDebugger] Imprint reset');
      }
    };

    return {
      newImprintStore,
      imprintStore,
      isExpanded,
      currentImprint,
      maxColorsTracking,
      uniqueColorsCount,
      availableLocations,
      availableColors,
      exportData,
      toggleExpanded,
      testValidation,
      testAddLine,
      testResetImprint
    };
  }
};
</script>

<style scoped lang="scss">
.state-debugger {
  position: fixed;
  bottom: 20px;
  right: 20px;
  max-width: 600px;
  max-height: 80vh;
  background: #1e1e1e;
  color: #d4d4d4;
  border: 2px solid #0D72B1;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  font-family: 'Courier New', monospace;
  font-size: 12px;
  z-index: 9999;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.debugger-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #0D72B1;
  color: white;
  cursor: pointer;
  user-select: none;

  h3 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
  }

  .toggle-icon {
    font-size: 20px;
    font-weight: bold;
  }

  &:hover {
    background: #0054A3;
  }
}

.debugger-content {
  overflow-y: auto;
  padding: 16px;
  flex: 1;
}

.debug-section {
  margin-bottom: 20px;
  padding: 12px;
  background: #2d2d2d;
  border-radius: 4px;
  border: 1px solid #3e3e3e;

  h4 {
    margin: 0 0 10px 0;
    color: #4ec9b0;
    font-size: 13px;
    font-weight: bold;
  }

  pre {
    margin: 0;
    padding: 8px;
    background: #1e1e1e;
    border-radius: 3px;
    overflow-x: auto;
    white-space: pre-wrap;
    word-wrap: break-word;
    color: #ce9178;
  }
}

.debug-subsection {
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }

  strong {
    display: block;
    margin-bottom: 4px;
    color: #9cdcfe;
    font-size: 12px;
  }

  pre {
    margin: 0;
    padding: 6px;
    font-size: 11px;
  }
}

.actions {
  button {
    margin-right: 8px;
    margin-bottom: 8px;
    padding: 6px 12px;
    background: #0D72B1;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 11px;
    font-weight: 600;

    &:hover {
      background: #0054A3;
    }

    &:active {
      transform: scale(0.98);
    }
  }
}

/* Scrollbar styling */
.debugger-content::-webkit-scrollbar {
  width: 8px;
}

.debugger-content::-webkit-scrollbar-track {
  background: #1e1e1e;
}

.debugger-content::-webkit-scrollbar-thumb {
  background: #0D72B1;
  border-radius: 4px;

  &:hover {
    background: #0054A3;
  }
}
</style>
